import { Component, HostListener, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { PropertyService } from '../../properties/service/property.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { Table, TableModule } from 'primeng/table';
import { CreateAmenityComponent } from '../create-amenity/create-amenity.component';
import { Subject, debounceTime } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';

@Component({
  selector: 'app-amenities',
  templateUrl: './amenities.component.html',
  standalone: true,
  imports: [ButtonModule, CommonModule, DialogModule, DropdownModule, FormsModule, TableModule, CreateAmenityComponent, ToastModule],
  styleUrls: ['./amenities.component.scss'],
  providers: [MessageService],
})
export class AmenitiesComponent implements OnInit {
  amenities: any[] = [];
  position: 'center' | 'right' = 'center';
  visible: boolean = false;
  displayDeleteModal: boolean = false;
  isEditMode: boolean = false;
  selectedAmenity: any = null;
  blockSpecialCharsPattern = /[.*+?^${}()|[\]\\]/g;
  totalRecords: number = 0;
  loading: boolean = false;
  categories: any[] = [];
  selectedCategory: any = null;
  searchQuery: string = '';
  first: number = 0;
  rows: number = 10;
  sortField: string = '';
  sortOrder: number = 1;
  private searchSubject: Subject<void> = new Subject();
  selectedAmenityId: string = '';
  userInfo: any = null;
  propertyId: string = '';
  constructor(
    private router: Router,
    private propertyService: PropertyService,
    private messageService: MessageService
  ) {
    this.searchSubject.pipe(debounceTime(1000)).subscribe(() => {
      this.loadAmenities();
    });
  }

  ngOnInit() {
    this.propertyService.getUserInfo().subscribe(user => {
      this.userInfo = user;
      this.propertyId = user?.property_id?.trim();
      if (this.propertyId) {
        this.loadAmenities();
      }
    });
  }

  showDialog(position: 'right', amenity: any = null): void {
    this.position = position;
    this.visible = true;
    this.selectedAmenity = amenity;
  }

  loadAmenities(event?: any) {
    if (!this.propertyId || this.propertyId.trim().length === 0) {
      this.loading = false;
      return;
    }
    this.loading = true;

    if (event) {
      this.first = event.first || 0;
      this.rows = event.rows || 10;
      this.sortField = event.sortField || '';
      this.sortOrder = event.sortOrder || 1;
    }
    const params = {
      page: this.first / this.rows + 1,
      limit: this.rows,
      sortField: this.sortField,
      sortOrder: this.sortOrder,
      category: this.selectedCategory || '',
      search: this.searchQuery.trim(),
      propertyId: this.propertyId,
    };

    this.propertyService.getAllAmenities(params).subscribe(
      (response: any) => {
        if (response) {
          this.amenities = response.items;
          this.totalRecords = response.total;
          this.loadCategories();
        }
        this.loading = false;
      },
      (error) => {
        console.error('Error loading amenities:', error);
        this.loading = false;
      }
    );
  }

  loadCategories(): void {
    this.propertyService.getAmenityCategories().subscribe(
      (response: any) => {
        if (response?.length) {
          this.categories = response.map((item: any) => ({
            label: item.name,
            value: item.name
          }));
        }
      },
      (error) => {
        console.error('Error loading amenities:', error);
      }
    );
  }

  onSearch() {
    this.first = 0;
    this.searchSubject.next();
  }

  onCategoryChange() {
    this.first = 0;
    this.loadAmenities();
  }

  clearFilters() {
    this.selectedCategory = null;
    this.searchQuery = '';
    this.loadAmenities();
  }

  onAmenitySaved() {
    this.visible = false;
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: this.isEditMode
        ? 'Amenity updated successfully!'
        : 'Amenity created successfully!',
    });
    this.loadAmenities();
  }

  editAmenity(amenity: any) {
    this.showDialog('right', amenity);
    this.selectedAmenity = amenity;
    this.isEditMode = true;
  }

  deleteAmenity(amenity: any) {
    this.selectedAmenityId = amenity._id;
    this.displayDeleteModal = true;
  }

  onCancel() {
    this.displayDeleteModal = false;
    this.selectedAmenity = null;
  }

  onConfirm() {
    this.propertyService.deleteAmenity(this.selectedAmenityId).subscribe(() => {
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Amenity deleted successfully!',
      });
      this.loadAmenities();
      this.displayDeleteModal = false;
    });
  }

  // Block on typing
  blockSpecialChars(event: KeyboardEvent) {
    console.log("BLOCK typing");
    if (this.blockSpecialCharsPattern.test(event.key)) {
      event.preventDefault();
    }
  }

  // Block on paste
  blockSpecialCharsOnPaste(event: ClipboardEvent) {
    console.log("BLOCK PASTE");
    const pasteData = event.clipboardData?.getData('text') || '';
    if (this.blockSpecialCharsPattern.test(pasteData)) {
      event.preventDefault();
    }
  }

  // Block on drop
  blockSpecialCharsOnDrop(event: DragEvent) {
    console.log("BLOCK Drop");
    const dropData = event.dataTransfer?.getData('text') || '';
    if (this.blockSpecialCharsPattern.test(dropData)) {
      event.preventDefault();
    }
  }

  // Final cleanup in case something sneaks in
  sanitizeInput(event: Event) {
    console.log("SANITIZE INPUT");
    const input = event.target as HTMLInputElement;
    if (this.blockSpecialCharsPattern.test(input.value)) {
      input.value = input.value.replace(this.blockSpecialCharsPattern, '');
      this.searchQuery = input.value; // keep model in sync
    }
  }
}
